import {inject, Injectable} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {TranslateService} from '@ngx-translate/core';
import {PAGE_TITLE_KEYS} from '@new/common/page-title.i18n';

/**
 * Central place for document title formatting and imperative updates.
 * Router-driven titles use i18n keys on routes (see `PAGE_TITLE_KEYS`) and
 * `AssociateTitleStrategy`. Pass **display strings** into `setFromSegments` (e.g. API names);
 * use `TranslateService.instant` for any static fallback labels.
 */
@Injectable({
  providedIn: 'root',
})
export class PageTitleService {
  private readonly documentTitle = inject(Title);
  private readonly translate = inject(TranslateService);

  /** Key for the product suffix in `assets/i18n` (`pageTitle.app`). */
  readonly appTitleKey = PAGE_TITLE_KEYS.app;

  getTranslatedAppTitle(): string {
    return this.translate.instant(this.appTitleKey);
  }

  /**
   * Builds `segment1 · segment2 · … · translatedAppTitle`. Empty segments are skipped.
   * With no segments, returns the translated app title only.
   */
  formatWithAppName(segments: readonly string[]): string {
    const parts = segments.map((s) => s.trim()).filter(Boolean);
    const app = this.getTranslatedAppTitle();
    if (parts.length === 0) {
      return app;
    }
    return `${parts.join(' · ')} · ${app}`;
  }

  /** Sets `document.title` using {@link formatWithAppName}. */
  setFromSegments(segments: readonly string[]): void {
    this.documentTitle.setTitle(this.formatWithAppName(segments));
  }

  /** Sets the browser tab to the translated product name only. */
  resetToAppDefault(): void {
    this.documentTitle.setTitle(this.getTranslatedAppTitle());
  }
}
